import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, rabPdfUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { HealthBadge } from "@/components/HealthBadge";
import { Paywall } from "@/components/Paywall";
import { AddProjectDialog } from "@/components/AddProjectDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CashFlowTab } from "@/components/tabs/CashFlowTab";
import { TukangTab } from "@/components/tabs/TukangTab";
import { ProgressTab } from "@/components/tabs/ProgressTab";
import { ReportTab } from "@/components/tabs/ReportTab";
import { InvoiceTab } from "@/components/tabs/InvoiceTab";
import { BillingRecap } from "@/components/BillingRecap";
import {
  ArrowLeft, Loader2, Wallet, TrendingUp, Receipt, Building2, Trash2, Lock,
  Wallet2, ListChecks, FileBarChart, Crown, MapPin, Calendar, Pencil, Briefcase, Download,
  ReceiptText, FileText,
} from "lucide-react";
import { rupiah, rupiahShort, fmtDate } from "@/lib/format";
import { defaultCatImage } from "@/lib/catimage";
import { toast } from "sonner";

function Stat({ label, value, color, sub }) {
  return (
    <Card className="p-4 border-slate-200 bg-white">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-mono font-bold text-lg sm:text-xl mt-0.5" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </Card>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isPremium } = useAuth();
  const [project, setProject] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("cashflow");
  const [editOpen, setEditOpen] = useState(false);
  const [backing, setBacking] = useState(false);
  const [invoiceSignal, setInvoiceSignal] = useState(0);
  const [recapSignal, setRecapSignal] = useState(0);

  const loadProject = useCallback(async () => {
    try {
      const [p, t, w] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/transactions`),
        api.get(`/projects/${id}/workers`),
      ]);
      setProject(p.data); setTransactions(t.data); setWorkers(w.data);
    } catch { toast.error("Proyek tidak ditemukan"); navigate("/dashboard"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { loadProject(); }, [loadProject]);

  useEffect(() => {
    api.get("/work-categories")
      .then((r) => { if (Array.isArray(r.data)) setCategories(r.data); })
      .catch(() => {});
  }, []);

  const refreshFinance = async () => {
    const [p, t, w] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/projects/${id}/transactions`),
      api.get(`/projects/${id}/workers`),
    ]);
    setProject(p.data); setTransactions(t.data); setWorkers(w.data);
  };

  const deleteProject = async () => {
    try { await api.delete(`/projects/${id}`); toast.success("Proyek dihapus"); navigate("/dashboard"); }
    catch { toast.error("Gagal menghapus proyek"); }
  };

  const downloadProjectBackup = async () => {
    setBacking(true);
    const tid = toast.loading("Menyiapkan backup proyek (termasuk foto)...");
    try {
      const res = await api.get(`/projects/${id}/backup/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      const slug = (project?.name || "proyek").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      a.download = `profinance-${slug}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup proyek berhasil diunduh", { id: tid });
    } catch {
      toast.error("Gagal membuat backup proyek", { id: tid });
    } finally { setBacking(false); }
  };

  if (loading || !project) {
    return (
      <div className="min-h-screen bg-slate-50"><Header />
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>
      </div>
    );
  }

  const s = project.summary;

  const catObj = categories.find((x) => (x.name || "").toLowerCase() === (project.category || "").toLowerCase());
  const catImg = catObj?.imageUrl ? fileUrl(catObj.imageUrl) : null;
  const chipImg = catImg || defaultCatImage(project.category);
  const headerImg = project.thumbnail ? fileUrl(project.thumbnail) : (catImg || defaultCatImage(project.category));
  const rabDoc = project.rab || {};
  const hasRab = !!(rabDoc && ((rabDoc.sections && rabDoc.sections.length) || project.status === "Prospek" || project.rabTotal));

  const openCreateInvoice = () => {
    if (!isPremium) { setTab("invoice"); return; }
    setTab("invoice");
    setInvoiceSignal((n) => n + 1);
  };

  const TabTrigger = ({ value, icon: Icon, label, premium, testid }) => (
    <TabsTrigger value={value} data-testid={testid} className="gap-1.5 data-[state=active]:bg-white data-[state=active]:text-amber-700 data-[state=active]:shadow-sm relative">
      <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{label}</span>
      {premium && !isPremium && <Lock className="w-3 h-3 text-amber-500 ml-0.5" />}
    </TabsTrigger>
  );

  return (
    <div className="min-h-screen bg-slate-50 pf-grain">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Semua Proyek
        </button>

        <Card className="overflow-hidden border-slate-200 bg-white mb-5">
          <div className="h-36 sm:h-44 relative bg-slate-100">
            {headerImg ? <img src={headerImg} alt={project.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Building2 className="w-12 h-12 text-slate-300" /></div>}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 flex items-end justify-between gap-3">
              <div className="min-w-0">
                <div className="mb-1.5"><HealthBadge marginPct={s.marginPct} size="sm" /></div>
                <h1 className="font-display text-xl sm:text-2xl font-extrabold text-white tracking-tight truncate">{project.name}</h1>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-white/80 text-xs mt-1">
                  <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {project.owner || "-"}</span>
                  {project.category && (
                    <span data-testid="project-category-chip" className="flex items-center gap-1">
                      <img src={chipImg} alt={project.category} className="w-4 h-4 rounded object-cover" />
                      {project.category}
                    </span>
                  )}
                  {project.companyName && project.companyName !== "-" && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {project.companyName}</span>}
                  {project.alamatProyek && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {project.alamatProyek}</span>}
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(project.targetSelesai)}</span>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button data-testid="btn-backup-project" onClick={downloadProjectBackup} disabled={backing} title="Backup proyek (.zip)" className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur hover:bg-emerald-500 flex items-center justify-center text-white transition-colors disabled:opacity-60">
                  {backing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                </button>
                <button data-testid="btn-edit-project" onClick={() => setEditOpen(true)} className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur hover:bg-amber-500 flex items-center justify-center text-white transition-colors"><Pencil className="w-4 h-4" /></button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><button data-testid="btn-delete-project" className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur hover:bg-red-500 flex items-center justify-center text-white transition-colors"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                  <AlertDialogContent className="bg-white">
                    <AlertDialogHeader><AlertDialogTitle>Hapus proyek "{project.name}"?</AlertDialogTitle><AlertDialogDescription>Semua transaksi, tukang, dan progress akan dihapus permanen.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={deleteProject} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4">
            <Stat label="Nilai Kontrak" value={rupiahShort(s.nominal)} color="#0f172a" sub={rupiah(s.nominal)} />
            <Stat label="Saldo Bersih" value={rupiahShort(s.balance)} color={s.balance >= 0 ? "#16a34a" : "#dc2626"} sub={`Realisasi ${s.realisasiPct.toFixed(0)}%`} />
            <Stat label="Sisa Tagihan" value={rupiahShort(s.sisaTagihan)} color="#2563eb" sub={`Terbayar ${rupiahShort(s.terbayar)}`} />
            <Stat label="Total Keluar" value={rupiahShort(s.totalOut)} color="#ea580c" sub={`${s.txCount} transaksi`} />
          </div>
        </Card>

        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <Button data-testid="btn-create-invoice" onClick={openCreateInvoice} className="bg-amber-600 hover:bg-amber-700 text-white gap-2">
            <ReceiptText className="w-4 h-4" /> Buat Invoice
          </Button>
          {hasRab && (
            <Button data-testid="btn-view-quotation" variant="outline" onClick={() => window.open(rabPdfUrl(project.id), "_blank")} className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50">
              <FileText className="w-4 h-4" /> Lihat Quotation (RAB)
            </Button>
          )}
        </div>

        <div className="mb-5">
          <BillingRecap projectId={project.id} refreshKey={recapSignal} />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-5 w-full bg-slate-100 p-1 h-auto">
            <TabTrigger value="cashflow" icon={Wallet2} label="Cash Flow" testid="tab-cashflow" />
            <TabTrigger value="tukang" icon={Wallet} label="Tukang" testid="tab-tukang" />
            <TabTrigger value="invoice" icon={ReceiptText} label="Invoice" premium testid="tab-invoice" />
            <TabTrigger value="progress" icon={ListChecks} label="Progress" premium testid="tab-progress" />
            <TabTrigger value="report" icon={FileBarChart} label="Report" premium testid="tab-report" />
          </TabsList>

          <TabsContent value="cashflow" className="mt-5">
            <CashFlowTab project={project} transactions={transactions} onChange={refreshFinance} />
          </TabsContent>
          <TabsContent value="tukang" className="mt-5">
            <TukangTab project={project} workers={workers} onChange={refreshFinance} />
          </TabsContent>
          <TabsContent value="invoice" className="mt-5">
            {isPremium ? <InvoiceTab project={project} createSignal={invoiceSignal} onChanged={() => setRecapSignal((n) => n + 1)} /> : (
              <Paywall testid="paywall-banner-invoice" title="Invoice & Proforma (Premium)"
                features={[
                  "Proforma Invoice untuk DP & termin pembayaran",
                  "Invoice final saat pelunasan/serah terima",
                  "Invoice Retensi untuk masa pemeliharaan",
                  "Hitung PPN & retensi otomatis (kaidah yang benar)",
                  "Riwayat invoice + status Draft/Terkirim/Lunas",
                  "Export PDF profesional & share ke WhatsApp",
                ]} />
            )}
          </TabsContent>
          <TabsContent value="progress" className="mt-5">
            {isPremium ? <ProgressTab project={project} /> : (
              <Paywall testid="paywall-banner-progress" title="Progress Pekerjaan (Premium)"
                features={[
                  "Item & sub-item pekerjaan berbobot (cost-loaded)",
                  "Catat & EDIT progress harian 0-100%",
                  "Impor RAB langsung dari Excel (.xlsx)",
                  "Kurva-S: Rencana vs Realisasi + Time Schedule",
                  "Baseline vs Revisi RAB (lacak deviasi negosiasi)",
                  "Portal Klien realtime + share ke WhatsApp",
                  "Upload foto dokumentasi (maks 10/entri)",
                ]} />
            )}
          </TabsContent>
          <TabsContent value="report" className="mt-5">
            {isPremium ? <ReportTab project={project} /> : (
              <Paywall testid="paywall-banner-report" title="Laporan & Export PDF (Premium)"
                features={[
                  "Dashboard analitik: pie & bar chart pengeluaran",
                  "Laporan Internal PDF profesional",
                  "Laporan Progress PDF + grafik Kurva-S",
                  "Tabel Deviasi RAB (Baseline vs Revisi) di PDF",
                  "Dokumentasi foto lapangan di laporan",
                ]} />
            )}
          </TabsContent>
        </Tabs>
      </main>

      <AddProjectDialog open={editOpen} onOpenChange={setEditOpen} project={project} onCreated={(p) => setProject(p)} />
    </div>
  );
}
